import React, { ChangeEvent, useEffect, useState } from 'react';

import {
  Button,
  CheckCircleFillIcon,
  Input,
  Label,
  Radio,
  RadioGroup,
  Select,
  Text,
  toastService,
} from '@thefarmersfront/kitchen';

import { useShallow } from 'zustand/react/shallow';

import { Bullet } from '~/components/common';

import { fetchGoodsCommerceFbkSkuCode, useProductModalOptions } from '~/hooks/product';
import { useLoadingStore, useProductStore, useUserInfoStore } from '~/store';

import { TOAST_MESSAGE } from '~/constants';

import type { TypeErrorResponseData } from '~/types/common.types';
import type { TypeProductModalRequiredInputState } from '~/types/product.types';

import styles from './ProductRegisterModalProductInfoSectionDefault.module.scss';

const ProductRegisterModalInfoSectionDefault = () => {
  const { setIsLoading } = useLoadingStore();

  const hasPermission = useUserInfoStore((state) => state.hasPermission);
  const hasPermission3PL = hasPermission([{ ownerType: '3PL' }]);
  const hasPermissionFBK = hasPermission([{ ownerType: 'FBK' }]);

  const productModalOptions = useProductModalOptions();

  const [
    productRegisterModalValue,
    setProductRegisterModalValue,
    productModalRequiredValueStatus,
    resetProductModalRequiredValueStatus,
    productModalSkuCodeStatus,
    setProductModalSkuCodeStatus,
    resetProductModalSkuCodeStatus,
    setFocusFieldRefs,
  ] = useProductStore(
    useShallow((state) => [
      state.productRegisterModalValue,
      state.setProductRegisterModalValue,
      state.productModalRequiredValueStatus,
      state.resetProductModalRequiredValueStatus,
      state.productModalSkuCodeStatus,
      state.setProductModalSkuCodeStatus,
      state.resetProductModalSkuCodeStatus,
      state.setFocusFieldRefs,
    ])
  );

  const [isProductCodeButtonDisabled, setIsProductCodeButtonDisabled] = useState(true);

  useEffect(() => {
    if (productRegisterModalValue.skuCode.length > 11) {
      setIsProductCodeButtonDisabled(false);
    } else {
      setIsProductCodeButtonDisabled(true);
    }
  }, [productRegisterModalValue.skuCode]);

  const handleChangeSkuCodeInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    setProductRegisterModalValue({
      ...productRegisterModalValue,
      skuCode: e.target.value,
    });

    resetProductModalSkuCodeStatus();
  };

  const handleClickProductCodeButton = async () => {
    try {
      setIsLoading(true);

      const result = await fetchGoodsCommerceFbkSkuCode(productRegisterModalValue.skuCode);

      if (result) {
        toastService.addToast({
          message: '상품코드가 확인되었습니다.',
        });

        setProductModalSkuCodeStatus({
          isValid: true,
          status: 'default',
          validationMessage: '',
        });
      } else {
        setProductModalSkuCodeStatus({
          isValid: false,
          status: 'error',
          validationMessage: '등록 가능한 상품코드가 아닙니다.',
        });
      }
    } catch (error) {
      toastService.addToast({
        type: 'error',
        message: (error as TypeErrorResponseData['error'])?.message || TOAST_MESSAGE.DEFAULT_ERROR,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleClickRequiredInput = (e: React.MouseEvent) => {
    resetProductModalRequiredValueStatus(e.currentTarget.id as keyof TypeProductModalRequiredInputState);
  };

  const handleClickSkuCodeInput = (e: React.MouseEvent) => {
    handleClickRequiredInput(e);

    setProductModalSkuCodeStatus({
      isValid: false,
      status: 'default',
      validationMessage: '',
    });
  };

  const handleChangeProductNameInput = (e: ChangeEvent<HTMLInputElement>) => {
    setProductRegisterModalValue({
      ...productRegisterModalValue,
      skuName: e.target.value,
    });

    resetProductModalRequiredValueStatus('skuName');
  };

  const handleClickCombineRadio = (e: ChangeEvent<HTMLInputElement>) => {
    setProductRegisterModalValue({
      ...productRegisterModalValue,
      combinedPackingAvailability: e.target.value as 'Y' | 'N',
    });
  };

  const handleBlurInput = (e: React.FocusEvent<HTMLInputElement>) => {
    setProductRegisterModalValue({
      ...productRegisterModalValue,
      [e.currentTarget.id]: e.target.value.trim(),
    });
  };

  return (
    <>
      <div className={styles['item-row']}>
        <Label
          className={styles['label']}
          title="상품코드"
          required
          tooltip={
            hasPermissionFBK
              ? {
                  description: (
                    <div className={styles['tooltip-description']}>파트너 오피스에 등록된 상품코드를 입력해주세요.</div>
                  ),
                  maxWidth: '145px',
                }
              : false
          }
        />
        {!hasPermissionFBK && (
          <div className={styles['sku-code-wrapper']}>
            <Bullet className={styles['sku-code-alt-message']} fill="#bcc4cc" />
            <Text className={styles['sku-code-alt-message']} size="medium" color="secondary">
              등록 시 자동 생성됩니다.
            </Text>
          </div>
        )}
        {hasPermissionFBK && (
          <div className={styles['sku-code-input-wrapper']}>
            <Input
              ref={(el) => {
                setFocusFieldRefs('skuCode', el);
              }}
              id="skuCode"
              className={styles['input']}
              type="text"
              size="small"
              name="skuCode"
              value={productRegisterModalValue.skuCode}
              status={productModalSkuCodeStatus.status}
              validationMessage={productModalSkuCodeStatus.validationMessage}
              rightIcon={productModalSkuCodeStatus.isValid && <CheckCircleFillIcon size={16} color="#029568" />}
              placeholder="12자 입력"
              onClick={handleClickSkuCodeInput}
              onChange={handleChangeSkuCodeInput}
              onBlur={handleBlurInput}
            />
            <Button
              className={styles['button']}
              size="small"
              color="secondary"
              disabled={isProductCodeButtonDisabled}
              onClick={handleClickProductCodeButton}
            >
              조회
            </Button>
          </div>
        )}
      </div>
      <div className={styles['item-row']}>
        <Label className={styles['label']} title="상품명" required />
        <div className={styles['sku-name-input-wrapper']}>
          <Input
            ref={(el) => {
              setFocusFieldRefs('skuName', el);
            }}
            id="skuName"
            className={styles['input']}
            type="text"
            size="small"
            name="skuName"
            value={productRegisterModalValue.skuName}
            count
            maxLength={40}
            placeholder="입력"
            status={productModalRequiredValueStatus.skuName.status}
            validationMessage={productModalRequiredValueStatus.skuName.message}
            onClick={handleClickRequiredInput}
            onChange={handleChangeProductNameInput}
            onBlur={handleBlurInput}
          />
        </div>
      </div>

      <div className={styles['item-row']}>
        <Label className={styles['label']} title="상온냉장 합포 여부" />
        <div className={`${styles['input-wrapper']} ${styles['radio-group']}`}>
          <RadioGroup
            name="combinedPackingType"
            value={productRegisterModalValue.combinedPackingAvailability}
            size="medium"
            onChange={handleClickCombineRadio}
          >
            <Radio value="Y" label="예" />
            <Radio value="N" label="아니오" />
          </RadioGroup>
        </div>
      </div>

      <div className={styles['item-row']}>
        <Label className={styles['label']} title="상품 사용여부" required={hasPermission3PL} />
        <div className={`${styles['input-wrapper']} ${styles['radio-group']}`}>
          <Select
            id="useMethodCode"
            options={productModalOptions?.productUsageStatus ?? []}
            disabled
            size="small"
            status={productModalRequiredValueStatus.useMethodCode?.status}
            validationMessage={productModalRequiredValueStatus.useMethodCode?.message}
            value={productModalOptions?.productUsageStatus.find(
              ({ value }) => value === productRegisterModalValue.useMethodCode
            )}
          />
        </div>
      </div>
    </>
  );
};

export default ProductRegisterModalInfoSectionDefault;
